import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { extrairMensagemErro, storageKeys } from '../services/api';
import { perfilAdministrativo, obterUsuarioSessao } from '../permissions';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { storage } from '../services/storage';
import { dialog } from '../services/dialog';
import { downloadBlob, viewBlob, getFilenameFromHeader } from '../services/file';

type ModuloAdmin =
  | 'dashboard'
  | 'equipa'
  | 'empresas'
  | 'utilizadores'
  | 'fornecedores'
  | 'planos'
  | 'subscricoes'
  | 'auditoria'
  | 'configuracoes';

type FiltrosAdmin = {
  busca: string;
  status: string;
  perfil: string;
  tenant_id: string;
  actor_tipo: string;
  acao: string;
  entidade: string;
  data_inicio: string;
  data_fim: string;
};

const modulos: Array<{ id: ModuloAdmin; label: string }> = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'equipa', label: 'Equipa da Plataforma' },
  { id: 'empresas', label: 'Empresas' },
  { id: 'utilizadores', label: 'Utilizadores' },
  { id: 'fornecedores', label: 'Fornecedores' },
  { id: 'planos', label: 'Planos' },
  { id: 'subscricoes', label: 'Subscrições' },
  { id: 'auditoria', label: 'Auditoria' },
  { id: 'configuracoes', label: 'Configurações' }
];

const statusClasse = (status?: string | boolean) => {
  if (status === true) return 'status-done';
  if (status === false) return 'muted';
  if (!status) return 'muted';
  if (['ATIVO', 'ATIVA', 'PAGA', 'CONFIRMADO', 'APROVADO'].includes(String(status))) return 'status-done';
  if (['EXPIRADO', 'SUSPENSO', 'SUSPENSA', 'CANCELADO', 'CANCELADA', 'INATIVO', 'ATRASADA', 'REJEITADO'].includes(String(status))) return 'status-blocked';
  if (['PENDENTE', 'EM_ANALISE', 'PENDENTE_ANALISE'].includes(String(status))) return 'status-warning';
  return '';
};

const dataCurta = (valor?: string | null) =>
  valor ? new Date(valor).toLocaleDateString('pt-PT') : 'sem data';

const moeda = (valor?: number | string, codigo = 'KZ') =>
  `${codigo} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const AdminGlobal: React.FC = () => {
  const usuarioSessao = obterUsuarioSessao();
  const perfilAtual = String(usuarioSessao?.perfil || '');
  const superAdmin = perfilAtual === 'SUPER_ADMIN';
  const pode = useCallback((recurso: ModuloAdmin | 'candidaturas' | 'pagamentos' | 'configuracoes_globais') => {
    if (superAdmin) return true;
    if (!perfilAdministrativo(perfilAtual)) return false;
    const permissoes: Record<string, string[]> = {
      dashboard: ['ADMIN_SAAS', 'ANALISTA_CANDIDATURAS', 'ANALISTA_FINANCEIRO', 'SUPORTE'],
      equipa: [],
      empresas: ['ADMIN_SAAS', 'SUPORTE'],
      utilizadores: ['ADMIN_SAAS', 'SUPORTE'],
      fornecedores: ['ADMIN_SAAS', 'SUPORTE'],
      planos: [],
      subscricoes: ['ANALISTA_FINANCEIRO'],
      auditoria: ['ADMIN_SAAS', 'SUPORTE'],
      configuracoes: [],
      candidaturas: ['ADMIN_SAAS', 'ANALISTA_CANDIDATURAS'],
      pagamentos: ['ANALISTA_FINANCEIRO'],
      configuracoes_globais: []
    };
    return (permissoes[recurso] || []).includes(perfilAtual);
  }, [perfilAtual, superAdmin]);
  const modulosVisiveis = useMemo(() => modulos.filter((modulo) => pode(modulo.id)), [pode]);
  const [moduloAtivo, setModuloAtivo] = useState<ModuloAdmin>('dashboard');
  const [dashboard, setDashboard] = useState<any>(null);
  const [administradores, setAdministradores] = useState<any[]>([]);
  const [auditoriaAdministrador, setAuditoriaAdministrador] = useState<any[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [utilizadores, setUtilizadores] = useState<any[]>([]);
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [vinculos, setVinculos] = useState<any[]>([]);
  const [planos, setPlanos] = useState<any[]>([]);
  const [subscricoes, setSubscricoes] = useState<any[]>([]);
  const [auditoria, setAuditoria] = useState<any[]>([]);
  const [candidaturas, setCandidaturas] = useState<any[]>([]);
  const [faturas, setFaturas] = useState<any[]>([]);
  const [pagamentos, setPagamentos] = useState<any[]>([]);
  const [infoPagamentos, setInfoPagamentos] = useState<any[]>([]);
  const [infoPagamentoForm, setInfoPagamentoForm] = useState({
    banco: '',
    titular_conta: '',
    iban: '',
    nif: '',
    referencia_pagamento: '',
    instrucoes: '',
    ativo: true
  });
  const [filtros, setFiltros] = useState<FiltrosAdmin>({ busca: '', status: '', perfil: '', tenant_id: '', actor_tipo: '', acao: '', entidade: '', data_inicio: '', data_fim: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [paginacoes, setPaginacoes] = useState<Record<string, MetadadosPaginacao>>({});

  const carregar = useCallback(async (pagina = 1) => {
    setCarregando(true);
    setErro(null);
    try {
      const vazio = { data: { dados: [] } };
      const statusEmpresa = ['ATIVA', 'SUSPENSA', 'EXPIRADA', 'CANCELADA'].includes(filtros.status) ? filtros.status : undefined;
      const statusUsuario = ['ATIVO', 'INATIVO', 'SUSPENSO', 'CONVITE_PENDENTE'].includes(filtros.status) ? filtros.status : undefined;
      const statusFornecedor = ['ATIVO', 'INATIVO', 'SUSPENSO'].includes(filtros.status) ? filtros.status : undefined;
      const statusSubscricao = ['ATIVA', 'SUSPENSA', 'EXPIRADA', 'CANCELADA'].includes(filtros.status) ? filtros.status : undefined;
      const statusFatura = ['PENDENTE', 'PAGA', 'ATRASADA', 'CANCELADA'].includes(filtros.status) ? filtros.status : undefined;
      const statusPagamento = ['PENDENTE', 'PENDENTE_ANALISE', 'APROVADO', 'REJEITADO', 'CONFIRMADO', 'FALHADO', 'ESTORNADO'].includes(filtros.status) ? filtros.status : undefined;
      const busca = filtros.busca || undefined;
      const tenant_id = filtros.tenant_id || undefined;
      const actor_tipo = filtros.actor_tipo || undefined;
      const acao = filtros.acao || undefined;
      const entidade = filtros.entidade || undefined;
      const data_inicio = filtros.data_inicio || undefined;
      const data_fim = filtros.data_fim || undefined;
      const perfil = filtros.perfil || undefined;
      const [
        dash,
        adminsResp,
        empresasResp,
        utilizadoresResp,
        fornecedoresResp,
        vinculosResp,
        planosResp,
        subsResp,
        auditoriaResp,
        candidaturasResp,
        faturasResp,
        pagamentosResp,
        infoPagamentosResp
      ] = await Promise.all([
        pode('dashboard') ? api.get('/admin/dashboard') : Promise.resolve(vazio),
        pode('equipa') ? api.get('/admin/equipa-plataforma', { params: { busca, status: statusUsuario, perfil, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('empresas') ? api.get('/admin/empresas', { params: { busca, status: statusEmpresa, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('utilizadores') ? api.get('/admin/utilizadores', { params: { busca, status: statusUsuario, tenant_id, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('fornecedores') ? api.get('/admin/fornecedores', { params: { busca, status: statusFornecedor, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('fornecedores') ? api.get('/admin/fornecedores/vinculos', { params: { status: statusFornecedor, tenant_id, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('planos') ? api.get('/admin/planos') : Promise.resolve(vazio),
        pode('subscricoes') ? api.get('/admin/subscricoes', { params: { status: statusSubscricao, tenant_id, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('auditoria') ? api.get('/admin/auditoria', { params: { pagina, limite: 20, tenant_id, actor_tipo, acao, entidade, data_inicio, data_fim } }) : Promise.resolve(vazio),
        pode('candidaturas') ? api.get('/admin/candidaturas', { params: { pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('pagamentos') ? api.get('/admin/faturas', { params: { status: statusFatura, tenant_id, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('pagamentos') ? api.get('/admin/pagamentos', { params: { status: statusPagamento, pagina, limite: 20 } }) : Promise.resolve(vazio),
        pode('configuracoes_globais') ? api.get('/admin/info-pagamento') : Promise.resolve(vazio)
      ]);

      setDashboard(dash.data.dados);
      setAdministradores(adminsResp.data.dados || []);
      setEmpresas(empresasResp.data.dados || []);
      setUtilizadores(utilizadoresResp.data.dados || []);
      setFornecedores(fornecedoresResp.data.dados || []);
      setVinculos(vinculosResp.data.dados || []);
      setPlanos(planosResp.data.dados || []);
      setSubscricoes(subsResp.data.dados || []);
      setAuditoria(auditoriaResp.data.dados || []);
      setCandidaturas(candidaturasResp.data.dados || []);
      setFaturas(faturasResp.data.dados || []);
      setPagamentos(pagamentosResp.data.dados || []);
      setPaginaAtual(pagina);
      setPaginacoes({
        equipa: adminsResp.data.paginacao || paginacaoVazia,
        empresas: empresasResp.data.paginacao || candidaturasResp.data.paginacao || paginacaoVazia,
        utilizadores: utilizadoresResp.data.paginacao || paginacaoVazia,
        fornecedores: fornecedoresResp.data.paginacao || vinculosResp.data.paginacao || paginacaoVazia,
        subscricoes: subsResp.data.paginacao || paginacaoVazia,
        auditoria: auditoriaResp.data.paginacao || paginacaoVazia,
        candidaturas: candidaturasResp.data.paginacao || paginacaoVazia,
        faturas: faturasResp.data.paginacao || paginacaoVazia,
        pagamentos: pagamentosResp.data.paginacao || paginacaoVazia
      });
      const infos = infoPagamentosResp.data.dados || [];
      setInfoPagamentos(infos);
      if (infos[0]) {
        setInfoPagamentoForm({
          banco: infos[0].banco || '',
          titular_conta: infos[0].titular_conta || '',
          iban: infos[0].iban || '',
          nif: infos[0].nif || '',
          referencia_pagamento: infos[0].referencia_pagamento || '',
          instrucoes: infos[0].instrucoes || '',
          ativo: Boolean(infos[0].ativo)
        });
      }
    } catch (erroAdmin: any) {
      setErro(extrairMensagemErro(erroAdmin, 'Erro ao carregar console SaaS.'));
    } finally {
      setCarregando(false);
    }
  }, [filtros.busca, filtros.status, filtros.perfil, filtros.tenant_id, filtros.actor_tipo, filtros.acao, filtros.entidade, filtros.data_inicio, filtros.data_fim, pode]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!modulosVisiveis.some((modulo) => modulo.id === moduloAtivo)) {
      setModuloAtivo(modulosVisiveis[0]?.id || 'dashboard');
    }
  }, [moduloAtivo, modulosVisiveis]);

  const executarAcao = async (acao: () => Promise<any>, fallback: string) => {
    setErro(null);
    setMensagem(null);
    try {
      const resposta = await acao();
      setMensagem(resposta.data?.mensagem || 'Ação administrativa executada com sucesso.');
      await carregar();
    } catch (erroAcao: any) {
      setErro(extrairMensagemErro(erroAcao, fallback));
    }
  };

  const resetarAcesso = async (usuarioId: string) => {
    const senha = await dialog.prompt('Senha temporária para o utilizador');
    if (!senha) return;
    await executarAcao(
      () => api.post(`/admin/utilizadores/${usuarioId}/reset-acesso`, { senha_temporaria: senha }),
      'Erro ao resetar acesso.'
    );
  };

  const criarAdministrador = async () => {
    const nome = await dialog.prompt('Nome do administrador');
    if (!nome) return;
    const email = await dialog.prompt('E-mail do administrador');
    if (!email) return;
    const perfil = await dialog.prompt('Perfil: ADMIN_SAAS, ANALISTA_CANDIDATURAS, ANALISTA_FINANCEIRO, SUPORTE ou SUPER_ADMIN', 'SUPORTE');
    if (!perfil) return;
    const senha = await dialog.prompt('Senha temporária com pelo menos 8 caracteres');
    if (!senha) return;
    await executarAcao(
      () => api.post('/admin/equipa-plataforma', { nome, email, perfil: perfil.toUpperCase(), senha, status: 'ATIVO' }),
      'Erro ao criar administrador da plataforma.'
    );
  };

  const editarAdministrador = async (admin: any) => {
    const nome = await dialog.prompt('Nome do administrador', admin.nome);
    if (!nome) return;
    const perfil = await dialog.prompt('Perfil administrativo', admin.perfil);
    if (!perfil) return;
    await executarAcao(
      () => api.put(`/admin/equipa-plataforma/${admin.id}`, { nome, perfil: perfil.toUpperCase() }),
      'Erro ao editar administrador da plataforma.'
    );
  };

  const resetarAcessoAdministrador = async (adminId: string) => {
    const senha = await dialog.prompt('Senha temporária para o administrador');
    if (!senha) return;
    await executarAcao(
      () => api.post(`/admin/equipa-plataforma/${adminId}/reset-acesso`, { senha_temporaria: senha }),
      'Erro ao redefinir acesso do administrador.'
    );
  };

  const carregarAuditoriaAdministrador = async (admin: any) => {
    setErro(null);
    setMensagem(null);
    try {
      const resposta = await api.get(`/admin/equipa-plataforma/${admin.id}/auditoria`, { params: { limite: 20 } });
      setAuditoriaAdministrador(resposta.data.dados || []);
      setModuloAtivo('equipa');
      setMensagem(`Auditoria carregada para ${admin.nome}.`);
    } catch (erroAcao: any) {
      setErro(extrairMensagemErro(erroAcao, 'Erro ao carregar auditoria do administrador.'));
    }
  };

  const rejeitarCandidatura = async (id: string) => {
    const motivo = await dialog.prompt('Motivo da rejeição');
    if (!motivo) return;
    await executarAcao(() => api.post(`/admin/candidaturas/${id}/rejeitar`, { motivo }), 'Erro ao rejeitar candidatura.');
  };

  const baixarDocumento = async (id: string) => {
    setErro(null);
    try {
      const resposta = await api.get(`/admin/candidaturas/${id}/documento`, { responseType: 'blob' });
      viewBlob(resposta.data);
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao baixar documento.'));
    }
  };

  const baixarComprovativo = async (id: string) => {
    setErro(null);
    try {
      const resposta = await api.get(`/admin/pagamentos/${id}/comprovativo`, { responseType: 'blob' });
      viewBlob(resposta.data);
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao abrir comprovativo.'));
    }
  };

  const exportarAuditoriaPdf = async () => {
    setErro(null);
    setMensagem(null);
    if (!filtros.data_inicio || !filtros.data_fim) {
      setErro('Informe data início e data fim para exportar auditoria.');
      return;
    }
    const inicio = new Date(`${filtros.data_inicio}T00:00:00`);
    const fim = new Date(`${filtros.data_fim}T00:00:00`);
    if (fim < inicio) {
      setErro('A data fim deve ser maior ou igual à data início.');
      return;
    }
    if (fim.getTime() - inicio.getTime() > 30 * 24 * 60 * 60 * 1000) {
      setErro('O intervalo máximo para exportação é de 30 dias.');
      return;
    }

    try {
      const resposta = await api.get('/admin/auditoria/export/pdf', {
        params: {
          data_inicio: filtros.data_inicio,
          data_fim: filtros.data_fim,
          tenant_id: filtros.tenant_id || undefined
        },
        responseType: 'blob'
      });
      const nome = getFilenameFromHeader(resposta.headers?.['content-disposition'], `auditoria-${filtros.tenant_id || 'global'}-${filtros.data_inicio}-${filtros.data_fim}.pdf`);
      downloadBlob(resposta.data, nome);
      setMensagem('PDF de auditoria gerado com sucesso.');
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao exportar auditoria em PDF.'));
    }
  };

  const rejeitarPagamento = async (id: string) => {
    const motivo = await dialog.prompt('Motivo da rejeição');
    if (!motivo) return;
    await executarAcao(() => api.post(`/admin/pagamentos/${id}/rejeitar`, { motivo }), 'Erro ao rejeitar pagamento.');
  };

  const salvarInfoPagamento = async () => {
    const atual = infoPagamentos[0];
    await executarAcao(
      () => atual ? api.put(`/admin/info-pagamento/${atual.id}`, infoPagamentoForm) : api.post('/admin/info-pagamento', infoPagamentoForm),
      'Erro ao guardar informações de pagamento.'
    );
  };

  const criarPlano = async () => {
    const nome = await dialog.prompt('Nome do plano');
    if (!nome) return;
    const codigo = await dialog.prompt('Código do plano');
    if (!codigo) return;
    const preco = Number(await dialog.prompt('Preço mensal em Kz') || 0);
    const max = Number(await dialog.prompt('Máximo de utilizadores') || 1);
    await executarAcao(
      () => api.post('/admin/planos', { nome, codigo, preco_mensal: preco, max_usuarios: max, moeda: 'KZ' }),
      'Erro ao criar plano.'
    );
  };

  const editarPlano = async (plano: any) => {
    const preco = Number(await dialog.prompt('Preço mensal em Kz', String(plano.preco_mensal)) || plano.preco_mensal);
    const max = Number(await dialog.prompt('Máximo de utilizadores', String(plano.max_usuarios)) || plano.max_usuarios);
    await executarAcao(
      () => api.put(`/admin/planos/${plano.id}`, { preco_mensal: preco, max_usuarios: max }),
      'Erro ao editar plano.'
    );
  };

  const alterarPlanoEmpresa = async (empresaId: string) => {
    const codigo = await dialog.prompt('Código do novo plano: BASE, CRESCIMENTO ou CORPORATIVO');
    if (!codigo) return;
    await executarAcao(
      () => api.post(`/admin/empresas/${empresaId}/alterar-plano`, { plano: codigo.toUpperCase(), dias: 30 }),
      'Erro ao alterar plano da empresa.'
    );
  };

  const metricas = dashboard
    ? [
        ['Empresas', dashboard.total_empresas],
        ['Ativas', dashboard.empresas_ativas],
        ['Suspensas', dashboard.empresas_suspensas],
        ['Utilizadores', dashboard.total_utilizadores],
        ['Fornecedores', dashboard.total_fornecedores],
        ['Subscrições ativas', dashboard.total_subscricoes_ativas]
      ]
    : [];

  const nomeAtorAuditoria = (evento: any) =>
    evento.actor_nome ||
    evento.usuario?.nome ||
    evento.fornecedor?.nome_empresa ||
    evento.admin?.nome ||
    'Sistema';

  return (
    <div className="workspace-page admin-console">
      <header className="page-header">
        <div>
          <span className="eyebrow">Console SaaS</span>
          <h1>Administração da plataforma Akzição</h1>
          <p>Gestão global de tenants, acessos, planos, subscrições e auditoria, separada do ciclo operacional de compras.</p>
        </div>
        <button className="botao botao-secundario" type="button" onClick={() => carregar(paginaAtual)}>
          Atualizar
        </button>
      </header>

      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}

      <section className="admin-toolbar">
        <div className="admin-tabs" role="tablist" aria-label="Módulos da console">
          {modulosVisiveis.map((modulo) => (
            <button
              key={modulo.id}
              className={moduloAtivo === modulo.id ? 'active' : ''}
              type="button"
              onClick={() => setModuloAtivo(modulo.id)}
            >
              {modulo.label}
            </button>
          ))}
        </div>
        <div className="admin-filters">
          <input
            aria-label="Pesquisar"
            placeholder="Pesquisar por nome, email, NIF..."
            value={filtros.busca}
            onChange={(evento) => setFiltros((atual) => ({ ...atual, busca: evento.target.value }))}
          />
          <input
            aria-label="Tenant"
            placeholder="tenant_id"
            value={filtros.tenant_id}
            onChange={(evento) => setFiltros((atual) => ({ ...atual, tenant_id: evento.target.value }))}
          />
          <select
            aria-label="Status"
            value={filtros.status}
            onChange={(evento) => setFiltros((atual) => ({ ...atual, status: evento.target.value }))}
          >
            <option value="">Todos os estados</option>
            <option value="ATIVO">ATIVO</option>
            <option value="ATIVA">ATIVA</option>
            <option value="SUSPENSO">SUSPENSO</option>
            <option value="SUSPENSA">SUSPENSA</option>
            <option value="INATIVO">INATIVO</option>
            <option value="CANCELADA">CANCELADA</option>
            <option value="PENDENTE">PENDENTE</option>
            <option value="PENDENTE_ANALISE">PENDENTE_ANALISE</option>
            <option value="APROVADO">APROVADO</option>
            <option value="REJEITADO">REJEITADO</option>
          </select>
          {superAdmin && (
            <select
              aria-label="Perfil administrativo"
              value={filtros.perfil}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, perfil: evento.target.value }))}
            >
              <option value="">Todos os perfis</option>
              <option value="SUPER_ADMIN">SUPER_ADMIN</option>
              <option value="ADMIN_SAAS">ADMIN_SAAS</option>
              <option value="ANALISTA_CANDIDATURAS">ANALISTA_CANDIDATURAS</option>
              <option value="ANALISTA_FINANCEIRO">ANALISTA_FINANCEIRO</option>
              <option value="SUPORTE">SUPORTE</option>
            </select>
          )}
        </div>
      </section>

      {carregando && (
        <div className="inline-loading">
          <div className="spinner"></div>
          <span>A carregar dados administrativos...</span>
        </div>
      )}

      {moduloAtivo === 'dashboard' && (
        <>
          <section className="metric-grid admin-metric-grid">
            {metricas.map(([label, value]) => (
              <div className="metric-card" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </section>

          <section className="operation-grid">
            <div className="panel">
              <div className="panel-heading">
                <h2>Candidaturas pendentes</h2>
                <span className="status-pill status-warning">
                  {candidaturas.filter((item) => item.status === 'PENDENTE_ANALISE').length}
                </span>
              </div>
              <div className="stacked-list">
                {candidaturas.slice(0, 6).map((candidatura) => (
                  <div className="list-row" key={candidatura.id}>
                    <span>{candidatura.nome_empresa}<small>{candidatura.tipo} · {candidatura.email}</small></span>
                    <strong><span className={`status-pill ${statusClasse(candidatura.status)}`}>{candidatura.status}</span></strong>
                  </div>
                ))}
                {candidaturas.length === 0 && <div className="empty-state compact-state">Sem candidaturas para análise.</div>}
              </div>
            </div>
            <div className="panel">
              <div className="panel-heading">
                <h2>Auditoria recente</h2>
                <span className="status-pill status-done">Rastreável</span>
              </div>
              <div className="stacked-list">
                {auditoria.slice(0, 6).map((evento) => (
                  <div className="list-row" key={evento.id}>
                    <span>{evento.acao}<small>{evento.entidade} · {evento.tipo}</small></span>
                    <strong>{evento.data_criacao ? new Date(evento.data_criacao).toLocaleTimeString('pt-PT') : '-'}</strong>
                  </div>
                ))}
                {auditoria.length === 0 && <div className="empty-state compact-state">Sem eventos recentes.</div>}
              </div>
            </div>
          </section>
        </>
      )}

      {moduloAtivo === 'equipa' && superAdmin && (
        <section className="operation-grid">
          <div className="panel">
            <div className="panel-heading">
              <h2>Equipa da Plataforma</h2>
              <span className="status-pill status-done">{administradores.length} administradores</span>
              <button className="botao botao-primario" type="button" onClick={criarAdministrador}>Criar</button>
            </div>
            <div className="table-shell">
              <table>
                <thead><tr><th>Nome</th><th>Email</th><th>Perfil</th><th>Estado</th><th>Último acesso</th><th>Criação</th><th>Ações</th></tr></thead>
                <tbody>
                  {administradores.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.nome}<small>{admin.tenant?.nome_empresa || 'Plataforma Akzição'}</small></td>
                      <td>{admin.email}</td>
                      <td>{admin.perfil}</td>
                      <td><span className={`status-pill ${statusClasse(admin.status)}`}>{admin.status}</span></td>
                      <td>{admin.ultimo_acesso ? new Date(admin.ultimo_acesso).toLocaleString('pt-PT') : 'sem acesso'}</td>
                      <td>{dataCurta(admin.data_criacao)}</td>
                      <td>
                        <div className="inline-actions">
                          <button className="botao botao-secundario" type="button" onClick={() => editarAdministrador(admin)}>Editar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/equipa-plataforma/${admin.id}/suspender`), 'Erro ao suspender administrador.')}>Suspender</button>
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/equipa-plataforma/${admin.id}/reativar`), 'Erro ao reativar administrador.')}>Reativar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => resetarAcessoAdministrador(admin.id)}>Senha</button>
                          <button className="botao botao-secundario" type="button" onClick={() => carregarAuditoriaAdministrador(admin)}>Auditoria</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {administradores.length === 0 && <tr><td colSpan={7}>Sem administradores para mostrar.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="panel">
            <div className="panel-heading">
              <h2>Auditoria individual</h2>
              <span className="status-pill status-done">{auditoriaAdministrador.length} eventos</span>
            </div>
            <div className="stacked-list">
              {auditoriaAdministrador.map((evento) => (
                <div className="list-row" key={evento.id}>
                  <span>{evento.acao}<small>{evento.admin_perfil || evento.admin?.perfil || evento.actor_tipo} · {evento.entidade}</small></span>
                  <strong>{evento.data_criacao ? new Date(evento.data_criacao).toLocaleString('pt-PT') : '-'}</strong>
                </div>
              ))}
              {auditoriaAdministrador.length === 0 && <div className="empty-state compact-state">Selecione Auditoria numa linha da equipa.</div>}
            </div>
          </div>
        </section>
      )}

      {moduloAtivo === 'empresas' && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Empresas</h2>
            <span className="status-pill status-done">Tenants globais</span>
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Empresa</th><th>Plano</th><th>Status</th><th>Utilização</th><th>Operação</th><th>Ações</th></tr></thead>
              <tbody>
                {empresas.map((empresa) => (
                  <tr key={empresa.id}>
                    <td>{empresa.nome_empresa}<small>{empresa.cnpj_nif} · fim {dataCurta(empresa.data_fim_plano)}</small></td>
                    <td>{empresa.plano}<small>{moeda(empresa.valor_mensalidade, empresa.moeda)}</small></td>
                    <td><span className={`status-pill ${statusClasse(empresa.status)}`}>{empresa.status}</span></td>
                    <td>{empresa.usuarios_ativos} / {empresa.max_usuarios}<small>{empresa._count?.requisicoes || 0} requisições</small></td>
                    <td>{empresa.status === 'ATIVA' ? 'Permitida' : 'Bloqueada pela subscrição'}</td>
                    <td>
                      <div className="inline-actions">
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/empresas/${empresa.id}/suspender`), 'Erro ao suspender empresa.')}>Suspender</button>
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/empresas/${empresa.id}/reativar`), 'Erro ao reativar empresa.')}>Reativar</button>
                        <button className="botao botao-secundario" type="button" onClick={() => alterarPlanoEmpresa(empresa.id)}>Plano</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {empresas.length === 0 && <tr><td colSpan={6}>Sem empresas para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {moduloAtivo === 'utilizadores' && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Utilizadores</h2>
            <span className="status-pill status-done">Gestão global</span>
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Utilizador</th><th>Tenant</th><th>Perfil</th><th>Status</th><th>Acesso</th><th>Ações</th></tr></thead>
              <tbody>
                {utilizadores.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>{usuario.nome}<small>{usuario.email}</small></td>
                    <td>{usuario.tenant?.nome_empresa || usuario.tenant_id}</td>
                    <td>{usuario.perfil}<small>Alçada {usuario.nivel_alcada}</small></td>
                    <td><span className={`status-pill ${statusClasse(usuario.status)}`}>{usuario.status}</span></td>
                    <td>{usuario.ultimo_acesso ? new Date(usuario.ultimo_acesso).toLocaleString('pt-PT') : 'sem acesso'}</td>
                    <td>
                      <div className="inline-actions">
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/utilizadores/${usuario.id}/ativar`), 'Erro ao ativar utilizador.')}>Ativar</button>
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/utilizadores/${usuario.id}/bloquear`), 'Erro ao bloquear utilizador.')}>Bloquear</button>
                        <button className="botao botao-secundario" type="button" onClick={() => resetarAcesso(usuario.id)}>Reset</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {utilizadores.length === 0 && <tr><td colSpan={6}>Sem utilizadores para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {moduloAtivo === 'fornecedores' && (
        <section className="operation-grid">
          <div className="panel">
            <div className="panel-heading">
              <h2>Fornecedores globais</h2>
              <span className="status-pill status-done">Cadastro global</span>
            </div>
            <div className="table-shell">
              <table>
                <thead><tr><th>Fornecedor</th><th>Status</th><th>Vínculos</th><th>Ações</th></tr></thead>
                <tbody>
                  {fornecedores.map((fornecedor) => (
                    <tr key={fornecedor.id}>
                      <td>{fornecedor.nome_empresa}<small>{fornecedor.email} · {fornecedor.nif || 'sem NIF'}</small></td>
                      <td><span className={`status-pill ${statusClasse(fornecedor.status)}`}>{fornecedor.status}</span></td>
                      <td>{fornecedor._count?.vinculos || 0}</td>
                      <td>
                        <div className="inline-actions">
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/fornecedores/${fornecedor.id}/suspender`), 'Erro ao suspender fornecedor.')}>Suspender</button>
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/fornecedores/${fornecedor.id}/reativar`), 'Erro ao reativar fornecedor.')}>Reativar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/fornecedores/${fornecedor.id}/bloquear`), 'Erro ao bloquear fornecedor.')}>Bloquear</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {fornecedores.length === 0 && <tr><td colSpan={4}>Sem fornecedores para mostrar.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="panel">
            <div className="panel-heading">
              <h2>Vínculos fornecedor-tenant</h2>
              <span className="status-pill status-done">Consulta global</span>
            </div>
            <div className="table-shell">
              <table>
                <thead><tr><th>Fornecedor</th><th>Empresa</th><th>Status</th><th>Efeito</th></tr></thead>
                <tbody>
                  {vinculos.slice(0, 12).map((vinculo) => (
                    <tr key={vinculo.id}>
                      <td>{vinculo.fornecedor?.nome_empresa || vinculo.fornecedor_id}</td>
                      <td>{vinculo.tenant?.nome_empresa || vinculo.tenant_id}</td>
                      <td><span className={`status-pill ${statusClasse(vinculo.status)}`}>{vinculo.status}</span></td>
                      <td>{vinculo.status === 'ATIVO' ? 'Pode responder a oportunidades.' : 'Sem acesso operacional ao tenant.'}</td>
                    </tr>
                  ))}
                  {vinculos.length === 0 && <tr><td colSpan={4}>Sem vínculos para mostrar.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {moduloAtivo === 'planos' && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Planos</h2>
            <span className="status-pill status-done">Catálogo comercial</span>
            <button className="botao botao-primario" type="button" onClick={criarPlano}>Criar plano</button>
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Plano</th><th>Limites</th><th>Preço</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {planos.map((plano) => (
                  <tr key={plano.id}>
                    <td>{plano.nome}<small>{plano.codigo} · {plano.descricao || 'sem descrição'}</small></td>
                    <td>{plano.max_usuarios} utilizadores<small>{plano.recursos || 'recursos configuráveis'}</small></td>
                    <td>{moeda(plano.preco_mensal, plano.moeda)}</td>
                    <td><span className={`status-pill ${statusClasse(plano.ativo)}`}>{plano.ativo ? 'ATIVO' : 'INATIVO'}</span></td>
                    <td>
                      <div className="inline-actions">
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/planos/${plano.id}/ativar`), 'Erro ao ativar plano.')}>Ativar</button>
                        <button className="botao botao-secundario" type="button" onClick={() => editarPlano(plano)}>Editar</button>
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/planos/${plano.id}/desativar`), 'Erro ao desativar plano.')}>Desativar</button>
                        <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/planos/${plano.id}/arquivar`), 'Erro ao arquivar plano.')}>Arquivar</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {planos.length === 0 && <tr><td colSpan={5}>Sem planos para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {moduloAtivo === 'subscricoes' && (
        <section className="operation-grid">
          <div className="panel">
            <div className="panel-heading">
              <h2>Subscrições</h2>
              <span className="status-pill status-done">Gestão contratual</span>
            </div>
            <div className="table-shell">
              <table>
                <thead><tr><th>Empresa</th><th>Plano</th><th>Status</th><th>Vigência</th><th>Ações</th></tr></thead>
                <tbody>
                  {subscricoes.map((sub) => (
                    <tr key={sub.id}>
                      <td>{sub.tenant?.nome_empresa || sub.tenant_id}<small>{moeda(sub.valor_mensal, sub.moeda)}</small></td>
                      <td>{sub.plano?.nome || sub.plano_id}</td>
                      <td><span className={`status-pill ${statusClasse(sub.status)}`}>{sub.status}</span></td>
                      <td>{dataCurta(sub.data_inicio)} - {dataCurta(sub.data_fim)}</td>
                      <td>
                        <div className="inline-actions">
                          <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/subscricoes/${sub.id}/renovar`, { dias: 30 }), 'Erro ao renovar subscrição.')}>Renovar</button>
                          {superAdmin && (
                            <>
                              <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/subscricoes/${sub.id}/suspender`), 'Erro ao suspender subscrição.')}>Suspender</button>
                              <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/subscricoes/${sub.id}/reativar`), 'Erro ao reativar subscrição.')}>Reativar</button>
                              <button className="botao botao-secundario" type="button" onClick={() => executarAcao(() => api.post(`/admin/subscricoes/${sub.id}/cancelar`), 'Erro ao cancelar subscrição.')}>Cancelar</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {subscricoes.length === 0 && <tr><td colSpan={5}>Sem subscrições para mostrar.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="panel">
            <div className="panel-heading">
              <h2>Faturas e pagamentos</h2>
              <span className="status-pill status-done">Financeiro SaaS</span>
            </div>
            <div className="stacked-list">
              {faturas.slice(0, 6).map((fatura) => (
                <div className="list-row" key={fatura.id}>
                  <span>{fatura.numero_fatura}<small>{fatura.tenant?.nome_empresa || fatura.tenant_id} · vence {dataCurta(fatura.data_vencimento)}</small></span>
                  <strong><span className={`status-pill ${statusClasse(fatura.status)}`}>{fatura.status}</span></strong>
                </div>
              ))}
              <div className="divider"></div>
              {pagamentos.slice(0, 12).map((pagamento) => {
                const podeDecidirPagamento = String(pagamento.status) === 'PENDENTE_ANALISE';
                const decisaoDefinitiva = ['CONFIRMADO', 'REJEITADO'].includes(String(pagamento.status));
                return (
                  <div className="list-row" key={pagamento.id}>
                    <span>
                      {pagamento.metodo || 'Pagamento'}
                      <small>{pagamento.fatura?.tenant?.nome_empresa || pagamento.fatura?.numero_fatura} · {pagamento.comprovativo_nome || 'sem comprovativo'}</small>
                    </span>
                    <strong><span className={`status-pill ${statusClasse(pagamento.status)}`}>{pagamento.status}</span></strong>
                    <div className="inline-actions">
                      {pagamento.comprovativo_url && <button className="botao botao-secundario" type="button" onClick={() => baixarComprovativo(pagamento.id)}>PDF</button>}
                      {podeDecidirPagamento && (
                        <>
                          <button className="botao botao-primario" type="button" onClick={() => executarAcao(() => api.post(`/admin/pagamentos/${pagamento.id}/aprovar`, { dias: 30 }), 'Erro ao aprovar pagamento.')}>Aprovar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => rejeitarPagamento(pagamento.id)}>Rejeitar</button>
                        </>
                      )}
                      {decisaoDefinitiva && <span className="status-pill status-done">Decisão definitiva</span>}
                    </div>
                  </div>
                );
              })}
              {faturas.length === 0 && pagamentos.length === 0 && <div className="empty-state compact-state">Sem faturação para mostrar.</div>}
              <Paginacao paginacao={paginacoes.pagamentos || paginacoes.faturas || paginacaoVazia} carregando={carregando} aoMudarPagina={carregar} />
            </div>
          </div>
        </section>
      )}

      {moduloAtivo === 'auditoria' && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Auditoria global</h2>
            <span className="status-pill status-done">Ações críticas</span>
          </div>
          <div className="admin-filters audit-filters">
            <select
              aria-label="Tipo de ator"
              value={filtros.actor_tipo}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, actor_tipo: evento.target.value }))}
            >
              <option value="">Todos os atores</option>
              <option value="USUARIO">Usuário interno</option>
              <option value="FORNECEDOR">Fornecedor</option>
              <option value="SISTEMA">Sistema</option>
              <option value="SUPER_ADMIN">Super Admin</option>
            </select>
            <input
              aria-label="Ação"
              placeholder="acao"
              value={filtros.acao}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, acao: evento.target.value }))}
            />
            <input
              aria-label="Entidade"
              placeholder="entidade"
              value={filtros.entidade}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, entidade: evento.target.value }))}
            />
            <input
              aria-label="Data início"
              type="date"
              value={filtros.data_inicio}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, data_inicio: evento.target.value }))}
            />
            <input
              aria-label="Data fim"
              type="date"
              value={filtros.data_fim}
              onChange={(evento) => setFiltros((atual) => ({ ...atual, data_fim: evento.target.value }))}
            />
            <button className="botao botao-primario" type="button" onClick={exportarAuditoriaPdf}>Exportar PDF</button>
          </div>
          <div className="table-shell">
            <table>
              <thead><tr><th>Ação</th><th>Ator</th><th>Tipo</th><th>Entidade</th><th>Contexto</th><th>Quando</th></tr></thead>
              <tbody>
                {auditoria.map((evento) => (
                  <tr key={evento.id}>
                    <td>{evento.acao}<small>{evento.descricao || 'sem descrição adicional'}</small></td>
                    <td>{evento.admin_perfil || evento.actor_tipo || 'USUARIO'}<small>{nomeAtorAuditoria(evento)}</small></td>
                    <td>{evento.tipo}</td>
                    <td>{evento.entidade}<small>{evento.entidade_id || 'plataforma'}</small></td>
                    <td>{evento.tenant?.nome_empresa || evento.usuario?.email || evento.fornecedor?.email || 'Akzição'}</td>
                    <td>{evento.data_criacao ? new Date(evento.data_criacao).toLocaleString('pt-PT') : 'sem data'}</td>
                  </tr>
                ))}
                {auditoria.length === 0 && <tr><td colSpan={6}>Sem eventos de auditoria para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {moduloAtivo === 'configuracoes' && (
        <section className="operation-grid">
          <div className="panel">
            <div className="panel-heading">
              <h2>Parâmetros globais</h2>
              <span className="status-pill status-done">Leitura administrativa</span>
            </div>
            <div className="config-grid">
              <label><strong>Banco</strong><input value={infoPagamentoForm.banco} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, banco: evento.target.value }))} /></label>
              <label><strong>Titular da conta</strong><input value={infoPagamentoForm.titular_conta} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, titular_conta: evento.target.value }))} /></label>
              <label><strong>IBAN/Número da conta</strong><input value={infoPagamentoForm.iban} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, iban: evento.target.value }))} /></label>
              <label><strong>NIF</strong><input value={infoPagamentoForm.nif} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, nif: evento.target.value }))} /></label>
              <label><strong>Referência</strong><input value={infoPagamentoForm.referencia_pagamento} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, referencia_pagamento: evento.target.value }))} /></label>
              <label><strong>Instruções</strong><textarea value={infoPagamentoForm.instrucoes} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, instrucoes: evento.target.value }))} /></label>
            </div>
            <div className="inline-actions">
              <label className="checkbox-line"><input type="checkbox" checked={infoPagamentoForm.ativo} onChange={(evento) => setInfoPagamentoForm((atual) => ({ ...atual, ativo: evento.target.checked }))} /> Ativo</label>
              <button className="botao botao-primario" type="button" onClick={salvarInfoPagamento}>Guardar dados de pagamento</button>
            </div>
          </div>
          <div className="panel">
            <div className="panel-heading">
              <h2>Candidaturas e documentos</h2>
              <span className="status-pill status-warning">{candidaturas.filter((item) => item.status === 'PENDENTE_ANALISE').length} pendentes</span>
            </div>
            <div className="table-shell">
              <table>
                <thead><tr><th>Tipo</th><th>Candidato</th><th>Documento</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                  {candidaturas.map((candidatura) => (
                    <tr key={candidatura.id}>
                      <td>{candidatura.tipo}</td>
                      <td>{candidatura.nome_empresa}<small>{candidatura.gerente_email || candidatura.email}</small></td>
                      <td>{candidatura.documento?.nome_original || 'sem documento'}<small>{candidatura.documento?.mime_type_real || ''}</small></td>
                      <td><span className={`status-pill ${statusClasse(candidatura.status)}`}>{candidatura.status}</span></td>
                      <td>
                        <div className="inline-actions">
                          <button className="botao botao-secundario" type="button" onClick={() => baixarDocumento(candidatura.id)}>Documento</button>
                          {candidatura.status === 'PENDENTE_ANALISE' && (
                            <>
                              <button className="botao botao-primario" type="button" onClick={() => executarAcao(() => api.post(`/admin/candidaturas/${candidatura.id}/aprovar`), 'Erro ao aprovar candidatura.')}>Aprovar</button>
                              <button className="botao botao-secundario" type="button" onClick={() => rejeitarCandidatura(candidatura.id)}>Rejeitar</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {candidaturas.length === 0 && <tr><td colSpan={5}>Sem candidaturas para mostrar.</td></tr>}
                </tbody>
              </table>
              <Paginacao paginacao={paginacoes.candidaturas || paginacaoVazia} carregando={carregando} aoMudarPagina={carregar} />
            </div>
          </div>
        </section>
      )}
      {!carregando && moduloAtivo !== 'configuracoes' && paginacoes[moduloAtivo] && (
        <Paginacao paginacao={paginacoes[moduloAtivo]} carregando={carregando} aoMudarPagina={carregar} />
      )}
    </div>
  );
};

export default AdminGlobal;
